import { useState } from "react";
import { Lock, LogOut, ExternalLink, ShieldCheck } from "lucide-react";
import { API } from "@/data/site";
import Logo from "@/components/Logo";

const UNLOCK_KEY = "exoplusbio-portal-unlocked";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function PortalView({ url, onLock }) {
  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-28 md:px-10" data-testid="portal-view">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Restricted access</p>
          <h1 className="font-display mt-2 text-3xl tracking-tight md:text-4xl">CRM & Invoice Portal</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => window.open(url, "_blank", "noopener")}
            data-testid="portal-open-new-tab"
            className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-3 text-xs font-medium text-white transition-colors hover:bg-[#0e8c8c]"
          >
            <ExternalLink className="h-4 w-4" /> Open in new tab
          </button>
          <button
            onClick={() => { sessionStorage.removeItem(UNLOCK_KEY); onLock(); }}
            data-testid="portal-lock-button"
            className="inline-flex items-center gap-2 border border-[#e4e9ec] px-5 py-3 text-xs font-medium transition-colors hover:border-[#0a0a0a]"
          >
            <LogOut className="h-4 w-4" /> Lock
          </button>
        </div>
      </div>
      <p className="font-lab mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#56636b]">
        <ShieldCheck className="h-3.5 w-3.5 text-[#0e8c8c]" /> Session unlocked on this device only · Close or lock when finished
      </p>
      <div className="mt-8 border border-[#e4e9ec] bg-white shadow-sm">
        <iframe
          src={url}
          title="CRM & Invoice Portal"
          data-testid="portal-iframe"
          className="h-[76vh] w-full"
        />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-[#56636b]" data-testid="portal-fallback-note">
        If the portal does not load in this frame, use "Open in new tab" above.
      </p>
    </div>
  );
}

function LockScreen({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/portal/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      sessionStorage.setItem(UNLOCK_KEY, "1");
      onUnlock(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[82vh] items-center justify-center px-5 py-20" data-testid="portal-lock-screen">
      <form onSubmit={submit} className="w-full max-w-sm border border-[#e4e9ec] bg-white p-8 shadow-sm">
        <Logo />
        <div className="mt-8 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#e4e9ec] bg-[#e3f2f0]/50">
          <Lock className="h-5 w-5 text-[#0e8c8c]" />
        </div>
        <h1 className="font-display mt-5 text-3xl tracking-tight">CRM Portal</h1>
        <p className="font-lab mt-2 text-[10px] uppercase tracking-[0.16em] text-[#56636b]">Password protected · Authorized team only</p>
        <input
          type="password"
          required
          placeholder="Portal password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="portal-password-input"
          className="mt-7 w-full border border-[#e4e9ec] px-4 py-3 text-sm outline-none transition-colors focus:border-[#0e8c8c]"
        />
        {error && <p className="mt-4 border border-[#b3261e]/30 bg-[#b3261e]/5 px-3 py-2 text-xs text-[#b3261e]" data-testid="portal-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="portal-unlock-button"
          className="mt-6 w-full bg-[#0a0a0a] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#0e8c8c] disabled:opacity-50"
        >
          {busy ? "Verifying…" : "Unlock portal"}
        </button>
        <p className="font-lab mt-6 text-[9px] uppercase tracking-[0.14em] text-[#56636b]">
          5 failed attempts = 15 minute lockout
        </p>
      </form>
    </div>
  );
}

export default function PortalPage() {
  const [unlocked, setUnlocked] = useState(sessionStorage.getItem(UNLOCK_KEY) === "1");
  const [url, setUrl] = useState(process.env.REACT_APP_PORTAL_URL || "https://crm-invoice-portal.emergent.host");
  return unlocked ? <PortalView url={url} onLock={() => setUnlocked(false)} /> : <LockScreen onUnlock={(u) => { setUrl(u); setUnlocked(true); }} />;
}